import React from "react";
import {
  COULEUR,
  LARGEUR,
  Figure,
  PhraseAbsence,
  bornes,
  couperLibelle,
  echelle,
  estLibelle,
  estNombre,
  formatNombre,
} from "./_commun";

// Ecart — la différence entre deux périodes, DÉCOMPOSÉE PAR CAUSE (cascade).
//
// Ce qu'elle apprend (registre) : « la décomposition d'une différence par
// cause — nuits vides, commissions, charges ». Aucun écran ne compare deux
// périodes : il faut ouvrir deux mois et soustraire de tête. Ici : la barre
// d'avant, chaque cause qui fait monter ou descendre, la barre d'après.
//
// ══ FORME DES DONNÉES ═══════════════════════════════════════════════════════
//
//   {
//     unite?: "€" | string,
//     avant:  { label: "juillet", total: 3200 },
//     apres:  { label: "août",    total: 2360 },
//     causes: [ { label: "deux nuits vides de plus", delta: -540 }, … ]  // ≥ 1
//   }
//
// ⚠️ COHÉRENCE EXIGÉE : Σ delta doit valoir apres.total − avant.total (à 0,5
// près). Sinon la cascade ne « tombe » pas sur la barre d'après, et un dessin
// qui tombe faux est pire qu'une phrase : on rend une phrase qui le dit.

const H = 210;
const MARGE = { gauche: 40, droite: 10, haut: 24, bas: 40 };
const TOLERANCE = 0.5;

function validerEcart(d) {
  if (!d || typeof d !== "object")
    return { erreur: "pas de données sur cette période" };
  const { avant, apres, causes } = d;
  const periodeOk = (p) =>
    p && typeof p === "object" && estLibelle(p.label) && estNombre(p.total);
  if (!periodeOk(avant) || !periodeOk(apres))
    return { erreur: "il manque une des deux périodes à comparer" };
  if (!Array.isArray(causes) || causes.length === 0)
    return { erreur: "l'écart n'est pas décomposé — aucune cause fournie" };
  if (
    !causes.every(
      (c) =>
        c && typeof c === "object" && estLibelle(c.label) && estNombre(c.delta),
    )
  )
    return { erreur: "données incomplètes — une cause n'a pas de montant" };
  const somme = causes.reduce((s, c) => s + c.delta, 0);
  const ecart = apres.total - avant.total;
  if (Math.abs(somme - ecart) > TOLERANCE)
    return {
      erreur: `la décomposition ne tombe pas juste (causes ${formatNombre(somme, d.unite, { avecSigne: true })}, écart ${formatNombre(ecart, d.unite, { avecSigne: true })}) — rien dessiné`,
    };
  return { avant, apres, causes, ecart, unite: d.unite };
}

export default function Ecart({ titre, donnees }) {
  const v = validerEcart(donnees);
  if (v.erreur) return <PhraseAbsence titre={titre} texte={v.erreur} />;
  const { avant, apres, causes, ecart, unite } = v;

  // Les barres : avant, chaque cause (flottante), après.
  const barres = [{ type: "total", label: avant.label, de: 0, a: avant.total }];
  let cumul = avant.total;
  for (const c of causes) {
    barres.push({
      type: "cause",
      label: c.label,
      de: cumul,
      a: cumul + c.delta,
      delta: c.delta,
    });
    cumul += c.delta;
  }
  barres.push({ type: "total", label: apres.label, de: 0, a: apres.total });

  const n = barres.length;
  const largeurUtile = LARGEUR - MARGE.gauche - MARGE.droite;
  const pas = largeurUtile / n;
  const largeurBarre = Math.min(pas * 0.62, 56);
  const [yMin, yMax] = bornes(
    barres.flatMap((b) => [b.de, b.a]),
    { inclureZero: true },
  );
  const y = echelle(yMin, yMax, H - MARGE.bas, MARGE.haut);
  const maxChars = Math.max(6, Math.floor(pas / 5.2));

  const detail = causes
    .map(
      (c) => `${c.label} ${formatNombre(c.delta, unite, { avecSigne: true })}`,
    )
    .join(", ");
  const conclusion = `${titre} : ${formatNombre(ecart, unite, { avecSigne: true })} entre ${avant.label} et ${apres.label} — ${detail}`;

  return (
    <Figure forme="ecart" titre={titre} conclusion={conclusion} hauteur={H}>
      {/* Le zéro, pour que les totaux aient un sol. */}
      <line
        x1={MARGE.gauche}
        x2={LARGEUR - MARGE.droite}
        y1={y(0)}
        y2={y(0)}
        stroke={COULEUR.bord}
      />

      {barres.map((b, i) => {
        const xc = MARGE.gauche + pas * (i + 0.5);
        const haut = y(Math.max(b.de, b.a));
        const bas = y(Math.min(b.de, b.a));
        const couleur =
          b.type === "total"
            ? COULEUR.navy
            : b.delta < 0
              ? COULEUR.terra
              : COULEUR.vert;
        const valeur =
          b.type === "total"
            ? formatNombre(b.a, unite)
            : formatNombre(b.delta, unite, { avecSigne: true });
        const lignes = couperLibelle(b.label, maxChars);
        return (
          <g key={`${i}-${b.label}`} data-testid={`ecart-barre-${b.type}`}>
            <rect
              x={xc - largeurBarre / 2}
              y={haut}
              width={largeurBarre}
              height={Math.max(bas - haut, 1)}
              fill={couleur}
              rx="2"
            />
            {/* Le fil qui relie une barre à la suivante : la cascade se lit. */}
            {i < n - 1 ? (
              <line
                x1={xc + largeurBarre / 2}
                x2={xc + pas - largeurBarre / 2}
                y1={y(b.a)}
                y2={y(b.a)}
                stroke={COULEUR.grisD}
                strokeDasharray="2 2"
              />
            ) : null}
            <text
              x={xc}
              y={haut - 4}
              textAnchor="middle"
              fontSize="10.5"
              fontWeight="600"
              fill={couleur === COULEUR.vert ? COULEUR.navyD : couleur}
            >
              {valeur}
            </text>
            {lignes.map((l, k) => (
              <text
                key={k}
                x={xc}
                y={H - MARGE.bas + 13 + k * 11}
                textAnchor="middle"
                fontSize="9.5"
                fill={COULEUR.gris}
              >
                {l}
              </text>
            ))}
          </g>
        );
      })}

      {/* L'écart total, écrit une fois, en haut à droite. */}
      <text
        x={LARGEUR - MARGE.droite}
        y={MARGE.haut - 10}
        textAnchor="end"
        fontSize="12"
        fontWeight="600"
        fill={ecart < 0 ? COULEUR.terra : COULEUR.navyD}
      >
        {formatNombre(ecart, unite, { avecSigne: true })}
      </text>
    </Figure>
  );
}
